import React, {
  useCallback,
  useEffect,
  useState
} from "react";

import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Loader from "../../components/Loader";

import authService from "../../services/authService";
import settingsService from "../../services/settingsService";

const DealerDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        }

        const res =
          await settingsService.getDashboard();

        if (res?.success) {
          setDashData(
            res.dashboard || {}
          );
        }
      } catch (error) {
        if (!silent) {
          toast.error(
            error.response?.data?.message ||
              "Failed to load dashboard data."
          );
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    setUser(
      authService.getCurrentUser()
    );

    fetchDashboardData();
  }, [fetchDashboardData]);

  const toggleSidebar = () =>
    setSidebarOpen(
      (previous) => !previous
    );

  const closeSidebar = () =>
    setSidebarOpen(false);

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={closeSidebar}
        />

        <main className="flex-grow-1 p-3 p-lg-4 overflow-auto">
          <div className="container-fluid">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4">
              <div>
                <h2 className="fw-bold text-dark mb-1">
                  Dealer Portal
                </h2>

                <p className="text-secondary mb-0">
                  Welcome back,{" "}
                  <span className="fw-semibold text-primary">
                    {user?.full_name ||
                      user?.username}
                  </span>
                  !
                </p>
              </div>

              <button
                type="button"
                className="btn btn-light border btn-sm mt-3 mt-sm-0 align-self-start align-self-sm-center px-3"
                onClick={() =>
                  fetchDashboardData()
                }
              >
                <i className="bi bi-arrow-clockwise me-1" />
                Refresh Data
              </button>
            </div>

            {loading ? (
              <Loader message="Loading Dealer dashboard..." />
            ) : (
              <>
                <div className="dealer-hero mb-4">
                  <div>
                    <span className="dealer-eyebrow">
                      DEALER OPERATIONS
                    </span>

                    <h3 className="mb-2">
                      Your shop network, one control desk
                    </h3>

                    <p className="mb-0">
                      Create shops, distribute coins and monitor the
                      players connected to your network.
                    </p>
                  </div>

                  <div className="admin-hero-actions">
                    <Link
                      to="/shops"
                      className="btn btn-light"
                    >
                      <i className="bi bi-shop me-2" />
                      Manage Shops
                    </Link>

                    <Link
                      to="/transfer-shop"
                      className="btn btn-warning"
                    >
                      <i className="bi bi-arrow-left-right me-2" />
                      Transfer Coins
                    </Link>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  {[
                    [
                      "Dealer Wallet",
                      `🪙 ${Number(
                        dashData?.wallet ??
                          user?.wallet ??
                          0
                      ).toLocaleString(
                        "en-IN"
                      )}`,
                      "bi-wallet2",
                      "tone-success"
                    ],
                    [
                      "Total Shops",
                      dashData?.total_shops ??
                        0,
                      "bi-shop",
                      "tone-primary"
                    ],
                    // [
                    //   "Network Players",
                    //   dashData?.total_players ??
                    //     0,
                    //   "bi-people-fill",
                    //   "tone-info"
                    // ]
                  ].map(
                    ([
                      label,
                      value,
                      icon,
                      tone
                    ]) => (
                      <div
                        className="col-12 col-sm-6 col-xl-4"
                        key={label}
                      >
                        <div className="admin-stat-card dealer-stat-card h-100">
                          <span
                            className={`admin-stat-icon ${tone}`}
                          >
                            <i
                              className={`bi ${icon}`}
                            />
                          </span>

                          <div>
                            <div className="admin-stat-label">
                              {label}
                            </div>

                            <div className="admin-stat-value dealer-stat-value">
                              {value}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-8">
                    <div className="admin-panel h-100">
                      <div className="admin-panel-head">
                        <div>
                          <h5>
                            Dealer Workspace
                          </h5>

                          <p>
                            Fast access to your
                            most-used shop
                            operations.
                          </p>
                        </div>

                        <span className="admin-panel-icon primary">
                          <i className="bi bi-grid" />
                        </span>
                      </div>

                      <div className="dealer-action-grid">
                        <Link
                          to="/shops"
                          className="dealer-action-card"
                        >
                          <i className="bi bi-shop" />

                          <div>
                            <strong>
                              Shop Management
                            </strong>

                            <span>
                              Create, review and
                              control your own
                              shops.
                            </span>
                          </div>

                          <i className="bi bi-arrow-right" />
                        </Link>

                        <Link
                          to="/transfer-shop"
                          className="dealer-action-card"
                        >
                          <i className="bi bi-send" />

                          <div>
                            <strong>
                              Transfer to Shop
                            </strong>

                            <span>
                              Allocate coins from
                              your dealer wallet.
                            </span>
                          </div>

                          <i className="bi bi-arrow-right" />
                        </Link>

                        <Link
                          to="/reports"
                          className="dealer-action-card"
                        >
                          <i className="bi bi-bar-chart" />

                          <div>
                            <strong>
                              Reports
                            </strong>

                            <span>
                              Review bets,
                              transfers and wallet
                              activity.
                            </span>
                          </div>

                          <i className="bi bi-arrow-right" />
                        </Link>

                        <Link
                          to="/settings"
                          className="dealer-action-card"
                        >
                          <i className="bi bi-person-gear" />

                          <div>
                            <strong>
                              Profile & Settings
                            </strong>

                            <span>
                              Manage avatar and
                              your signed-in
                              session.
                            </span>
                          </div>

                          <i className="bi bi-arrow-right" />
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-xl-4">
                    <div className="admin-panel h-100">
                      <div className="admin-panel-head">
                        <div>
                          <h5>
                            Network Health
                          </h5>

                          <p>
                            Live dealer-scoped overview.
                          </p>
                        </div>

                        <span className="admin-panel-icon success">
                          <i className="bi bi-activity" />
                        </span>
                      </div>

                      <div className="dealer-health-list">
                        <div>
                          <span>
                            Shops under you
                          </span>

                          <strong>
                            {dashData?.total_shops ??
                              0}
                          </strong>
                        </div>

                        <div>
                          <span>
                            Players in network
                          </span>

                          <strong>
                            {dashData?.total_players ??
                              0}
                          </strong>
                        </div>

                        <div>
                          <span>
                            Available wallet
                          </span>

                          <strong className="text-success">
                            🪙{" "}
                            {Number(
                              dashData?.wallet ??
                                user?.wallet ??
                                0
                            ).toLocaleString(
                              "en-IN"
                            )}
                          </strong>
                        </div>

                        <div>
                          <span>
                            Access scope
                          </span>

                          <strong>
                            Own network only
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

               
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DealerDashboard;
